import { html, LitElement, nothing, PropertyValues } from "lit";
import { ProductSelectionContext, productSelectionContextKey } from "../product-selection.context";
import { AvailableProductItem, TimeSlotItem } from "../../types";
import { consume } from "@lit/context";
import { customElement, property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { createRef, ref } from "lit/directives/ref.js";
import autoAnimate, { AnimationController } from "@formkit/auto-animate";
import { ModalRequestEventDetail } from "../../core/components/content-modal.element";
import { classMap } from "lit/directives/class-map.js";

@customElement("product-item")
export class ProductItemElement extends LitElement {

    private rootElement = createRef<HTMLElement>();
    private animationController: AnimationController | null = null;

    @consume({ context: productSelectionContextKey})
    private productSelectionContext! : ProductSelectionContext;

    @state()
    private selectedProduct: AvailableProductItem | null = null;

    @state()
    private selectedTimeSlot: TimeSlotItem | null = null;

    @property({ type: Object})
    public product: AvailableProductItem | null = null;

    override connectedCallback() {
        super.connectedCallback();

        this.productSelectionContext.selectedProduct$.subscribe({ next: (next) => this.selectedProduct = next });

        this.productSelectionContext.selectedTimeSlot$.subscribe({ next: (next) => this.selectedTimeSlot = next });
    }

    protected override firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        if(this.rootElement.value && !this.animationController) {
            this.animationController = autoAnimate(this.rootElement.value);
        }
    }

    protected override render() {
        if(!this.product) {
            return nothing;
        }

        const isSelected = this.selectedProduct !== null && this.selectedProduct.id === this.product.id;
        const itemClasses = {
            "cursor-pointer": isSelected === false,
        }

        return html`
            <div class="flex flex-col gap-2 ${classMap(itemClasses)}" ${ref(this.rootElement)}>
                <div class="flex flex-col gap-2">
                    <div class="flex items-stretch gap-2" @click="${() => this.productSelectionContext.setSelectedProduct(this.product)}">
                        <img class="w-20 object-contain object-top lg:object-center lg:w-36" src="${`api/public/product/${this.product.id}/image`}" />
                        <div class="flex flex-col">
                            <div>${this.product.name}</div>
                            <p class="text-sm">${this.product.shortDescription}</p>
                        </div>
                    </div>
                </div>
                ${when(isSelected, () => html`
                    <div class="flex flex-col gap-2">
                        <p class="text-sm">${this.product!.longDescription}</p>
                        <a href="#" class="text-mcr-purple hover:underline" @click="${() => this.showTermsAndConditions()}">Terms & Conditions</a>
                        <time-slot-picker .product="${this.product}"></time-slot-picker>
                        <product-form .product="${this.product}" .timeSlot="${this.selectedTimeSlot}"></product-form>
                    </div>
                `)}
            </div>
        `;
    }

    private showTermsAndConditions() {
        const event = new CustomEvent<ModalRequestEventDetail>("show-modal-requested", {
            detail: {
                title: "Terms & Conditions",
                content: html`<pre>${this.product!.terms}</pre>`
            },
            bubbles: true,
        });
        this.dispatchEvent(event);
    }


    protected override createRenderRoot() {
        return this;
    }

}